import React from 'react'
import { formatDuration, formatTime } from '../../../utils/date'
import { Element } from 'react-bulma-components'
import ReactTooltip from 'react-tooltip'
import { WeekSummaryEmployerRule } from '../../../../../backend/src/services/resources/missions/missions.summary.service.types'
import { formatCost } from '../../../utils/finance'

interface SummaryCellWrapperProps {
  /**
   * Content to display in the cell
   */
  children: React.ReactNode
  /**
   * Optional tooltip content
   */
  tooltipContent?: React.ReactNode
  /**
   * Optional tooltip ID
   */
  tooltipId?: string
  /**
   * Text color for the cell
   */
  textColor?: string
}

/**
 * Wrapper component for summary table cells that adds tooltip functionality
 * @param props - Component props
 * @returns Cell with optional tooltip
 */
export const SummaryCellWrapper: React.FC<SummaryCellWrapperProps> = ({
  children,
  tooltipContent,
  tooltipId,
  textColor,
}) => {
  const hasTooltip = Boolean(tooltipContent && tooltipId)
  const [isOver, setIsOver] = React.useState(false)

  return (
    <>
      {hasTooltip && isOver && (
        <ReactTooltip id={tooltipId} place="bottom">
          {tooltipContent}
        </ReactTooltip>
      )}
      <Element
        textColor={textColor || 'dark'}
        textWeight={textColor ? 'bold' : 'normal'}
        data-tip
        data-for={tooltipId}
        onMouseOver={hasTooltip ? () => setIsOver(true) : undefined}
        onMouseOut={hasTooltip ? () => setIsOver(false) : undefined}
      >
        {children}
      </Element>
    </>
  )
}

interface DurationCellProps
  extends Pick<SummaryCellWrapperProps, 'tooltipContent' | 'tooltipId' | 'textColor'> {
  /**
   * Duration in minutes
   */
  duration?: number | null
  /**
   * Optional fallback text when duration is 0
   * @default '- h -'
   */
  fallbackText?: string
}

/**
 * Component to display a duration in a consistent format
 * @param props - Component props
 * @returns Formatted duration or fallback text
 */
export const DurationCell: React.FC<DurationCellProps> = ({
  duration,
  fallbackText = '- h -',
  tooltipContent,
  tooltipId,
  textColor,
}) => {
  return (
    <SummaryCellWrapper tooltipContent={tooltipContent} tooltipId={tooltipId} textColor={textColor}>
      {!duration ? fallbackText : formatDuration(duration)}
    </SummaryCellWrapper>
  )
}

interface HourCellProps
  extends Pick<SummaryCellWrapperProps, 'tooltipContent' | 'tooltipId' | 'textColor'> {
  date?: Date | null
  fallbackText?: string
}

export const HourCell: React.FC<HourCellProps> = ({
  date,
  fallbackText = '- h -',
  textColor,
  tooltipContent,
  tooltipId,
}) => {
  return (
    <SummaryCellWrapper tooltipContent={tooltipContent} tooltipId={tooltipId} textColor={textColor}>
      {date ? formatTime(date) : fallbackText}
    </SummaryCellWrapper>
  )
}

interface CostCellProps
  extends Pick<SummaryCellWrapperProps, 'tooltipContent' | 'tooltipId' | 'textColor'> {
  cost?: number | null
  fallbackText?: string
  noCostAvailable?: boolean
}

export const CostCell: React.FC<CostCellProps> = ({
  cost,
  fallbackText = '0€',
  textColor,
  tooltipContent,
  tooltipId,
  noCostAvailable,
}) => {
  return (
    <SummaryCellWrapper tooltipContent={tooltipContent} tooltipId={tooltipId} textColor={textColor}>
      {cost && !noCostAvailable ? formatCost(cost) : noCostAvailable ? '-' : fallbackText}
    </SummaryCellWrapper>
  )
}

interface NumberCellProps
  extends Pick<SummaryCellWrapperProps, 'tooltipContent' | 'tooltipId' | 'textColor'> {
  number?: number | null
  fallbackText?: string
}

export const NumberCell: React.FC<NumberCellProps> = ({
  number,
  fallbackText = '0',
  tooltipContent,
  tooltipId,
  textColor,
}) => {
  return (
    <SummaryCellWrapper tooltipContent={tooltipContent} tooltipId={tooltipId} textColor={textColor}>
      {number ? number : fallbackText}
    </SummaryCellWrapper>
  )
}

interface EmployerRuleCellProps
  extends Pick<SummaryCellWrapperProps, 'tooltipContent' | 'tooltipId' | 'textColor'> {
  employerRule: WeekSummaryEmployerRule
  value: number | null
  fallbackTexts?: Record<WeekSummaryEmployerRule['kind'], string>
}

export const EmployerRuleCell: React.FC<EmployerRuleCellProps> = ({
  employerRule,
  value,
  textColor,
  fallbackTexts,
}) => {
  const Component = employerRule.kind === 'goal' ? NumberCell : DurationCell
  return (
    <Component
      number={value}
      duration={value}
      textColor={textColor}
      fallbackText={fallbackTexts?.[employerRule.kind]}
    />
  )
}
